var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import "theme-ui";
import * as yup from "yup";
import { NumberSchema, MixedSchema } from "yup";
import { DateTime } from "luxon";
import { PhoneNumberUtil, PhoneNumberFormat } from "google-libphonenumber";
class MoneySchemaType extends NumberSchema {
  constructor() {
    super();
    this.withMutation(() => {
      this.transform(function trans(value, originalValue) {
        if (originalValue === "")
          return void 0;
        if (this.isType(value))
          return value;
        return Number.parseFloat(originalValue.replace(/[,$]/g, ""));
      });
    });
  }
  _typeCheck(value) {
    if (Number.isNaN(value))
      return false;
    if (!value)
      return true;
    if (super._typeCheck(value) || !Number.isNaN(value)) {
      const found = value.toString().match(/^-?[0-9]+(\.[0-9]{1,2})?$/);
      return found && (found == null ? void 0 : found.length);
    }
    return false;
  }
}
let options$1 = {
  allowFour: false,
  isProdValidationEnabled: false
};
class SsnSchemaType extends MixedSchema {
  constructor(passedOptions) {
    super();
    __publicField(this, "format", (format) => {
      const method = format || "no-dash";
      if (method !== "dash" && method !== "no-dash") {
        throw new TypeError("Only valid options for format() are: dash, no-dash");
      }
      return this.transform((value) => {
        if (!value)
          return value;
        if (method === "no-dash")
          return value.replace(/-/g, "").trim();
        return `${value.substr(0, 3)}-${value.substr(3, 2)}-${value.substr(5)}`;
      });
    });
    options$1 = passedOptions;
    this.withMutation(() => {
      this.transform(function trans(value, originalValue) {
        if (this.isType(value))
          return value;
        return originalValue.replace(/-/g, "").trim();
      });
    });
  }
  _typeCheck(value) {
    const ssn2 = value.replace(/-/g, "").trim();
    const isNumericRegex = /^[0-9]+$/;
    if (!isNumericRegex.test(ssn2))
      return false;
    if (options$1.allowFour && ssn2.length === 4) {
      if (ssn2 === "0000")
        return false;
      return true;
    }
    if (ssn2.length !== 9)
      return false;
    const badSSN = [
      "078051120",
      "219099999",
      "123456789",
      "123121234",
      "321214321"
    ];
    if (badSSN.includes(ssn2))
      return false;
    const ssnFirstCharactersValidation = options$1.isProdValidationEnabled ? ssn2.slice(0, 3) === "000" : ssn2.slice(0, 3) === "000" || ssn2.slice(0, 3) === "666" || ssn2.slice(0, 1) === "9";
    if (ssnFirstCharactersValidation)
      return false;
    if (ssn2.slice(3, 5) === "00")
      return false;
    if (ssn2.slice(5, 9) === "0000")
      return false;
    const repeatedDigits = /(\d)\1\1\1\1\1\1\1\1/;
    return !repeatedDigits.test(ssn2);
  }
}
let options = {
  timeZone: void 0
};
const formatDate = (value, dateOptions) => {
  const zone = dateOptions == null ? void 0 : dateOptions.timeZone;
  if (value.includes("Z") && value.includes("T")) {
    return DateTime.fromISO(value, { zone });
  }
  const tmp = value.split("T")[0].split("-");
  if (tmp.length < 3)
    return false;
  if (tmp[0].length < 4)
    return false;
  const year = Number.parseInt(tmp[0], 10);
  const month = Number.parseInt(tmp[1], 10);
  const day = Number.parseInt(tmp[2], 10);
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
    return false;
  }
  const dateObject = { year, month, day };
  return DateTime.fromObject(dateObject, { zone });
};
class DateSchemaType extends MixedSchema {
  constructor(passedOptions) {
    super();
    options = passedOptions;
    this.withMutation(() => {
      this.transform(function trans(value, originalValue) {
        if (this.isType(value))
          return value;
        return originalValue.trim();
      });
    });
  }
  _typeCheck(value) {
    if (!value)
      return true;
    const date2 = formatDate(value, options);
    return date2 ? date2.isValid : false;
  }
  minAge(age, message) {
    return this.test("isAge", ({ path }) => message || `${path} must be older than ${age}`, (value) => {
      if (!value)
        return true;
      const today = DateTime.local().minus({ years: age }).startOf("day");
      let date2 = formatDate(value, options);
      if (!date2)
        return false;
      date2 = date2.startOf("day");
      return today >= date2;
    });
  }
  before(before, message) {
    const date2 = formatDate(before, options);
    if (!date2 || !date2.isValid)
      throw new TypeError("Date for before() must be format YYYY-MM-DD");
    return this.test("before", ({ path }) => message || `${path} must be before ${before}`, (value) => {
      if (!value)
        return true;
      const testDate = formatDate(value, options);
      if (!testDate)
        return false;
      return testDate.startOf("day") < date2.startOf("day");
    });
  }
  after(after, message) {
    const date2 = formatDate(after, options);
    if (!date2 || !date2.isValid)
      throw new TypeError("Date for after() must be format YYYY-MM-DD");
    return this.test("after", ({ path }) => message || `${path} must be after ${after}`, (value) => {
      if (!value)
        return true;
      const testDate = formatDate(value, options);
      if (!testDate)
        return false;
      return testDate.startOf("day") > date2.startOf("day");
    });
  }
  sameOrBefore(before, message) {
    const date2 = formatDate(before, options);
    if (!date2 || !date2.isValid)
      throw new TypeError("Date for sameOrBefore() must be format YYYY-MM-DD");
    return this.test("sameOrBefore", ({ path }) => message || `${path} must be before or same date as ${before}`, (value) => {
      if (!value)
        return true;
      const testDate = formatDate(value, options);
      if (!testDate)
        return false;
      return testDate.startOf("day") <= date2.startOf("day");
    });
  }
  sameOrAfter(after, message) {
    const date2 = formatDate(after, options);
    if (!date2 || !date2.isValid)
      throw new TypeError("Date for sameOrAfter() must be format YYYY-MM-DD");
    return this.test("sameOrAfter", ({ path }) => message || `${path} must be after or same date as ${after}`, (value) => {
      if (!value)
        return true;
      const testDate = formatDate(value, options);
      if (!testDate)
        return false;
      return testDate.startOf("day") >= date2.startOf("day");
    });
  }
  future(message) {
    return this.test("future", ({ path }) => message || `${path} must be a future date`, (value) => {
      if (!value)
        return true;
      const today = DateTime.local({ zone: options == null ? void 0 : options.timeZone });
      const date2 = formatDate(value, options);
      if (!date2)
        return false;
      return today.startOf("day") < date2.startOf("day");
    });
  }
  past(message) {
    return this.test("past", ({ path }) => message || `${path} must be a past date`, (value) => {
      if (!value)
        return true;
      const today = DateTime.local({ zone: options == null ? void 0 : options.timeZone });
      const date2 = formatDate(value, options);
      if (!date2)
        return false;
      return today.startOf("day") > date2.startOf("day");
    });
  }
  sameOrFuture(message) {
    return this.test("sameOrFuture", ({ path }) => message || `${path} must be a current or future date`, (value) => {
      if (!value)
        return true;
      const today = DateTime.local({ zone: options == null ? void 0 : options.timeZone });
      const date2 = formatDate(value, options);
      if (!date2)
        return false;
      return today.startOf("day") <= date2.startOf("day");
    });
  }
  sameOrPast(message) {
    return this.test("sameOrPast", ({ path }) => message || `${path} must be a current or past date`, (value) => {
      if (!value)
        return true;
      const today = DateTime.local({ zone: options == null ? void 0 : options.timeZone });
      const date2 = formatDate(value, options);
      if (!date2)
        return false;
      return today.startOf("day") >= date2.startOf("day");
    });
  }
  format() {
    return this.transform((value) => {
      if (!value)
        return value;
      const date2 = formatDate(value, options);
      if (!date2)
        return value;
      return date2.toFormat("yyyy-MM-dd");
    });
  }
}
const phoneUtil = PhoneNumberUtil.getInstance();
class PhoneSchemaType extends MixedSchema {
  constructor() {
    super();
    this.withMutation(() => {
      this.transform(function trans(value, originalValue) {
        if (this.isType(value))
          return value;
        return originalValue.toString().trim();
      });
    });
  }
  _typeCheck(value) {
    try {
      if (!value)
        return true;
      const phone2 = phoneUtil.parseAndKeepRawInput(value, "US");
      if (!phoneUtil.isPossibleNumber(phone2))
        return false;
      const regionCodeFromPhoneNumber = phoneUtil.getRegionCodeForNumber(phone2);
      return phoneUtil.isValidNumberForRegion(phone2, regionCodeFromPhoneNumber);
    } catch (err) {
      return false;
    }
  }
  format(type) {
    const method = type || "E164";
    if (method !== "E164" && method !== "INTERNATIONAL" && method !== "NATIONAL" && method !== "RFC3966") {
      throw new TypeError("Only valid options for format() are: E164, INTERNATIONAL, NATIONAL, and RFC3966");
    }
    const mapping = {
      E164: PhoneNumberFormat.E164,
      INTERNATIONAL: PhoneNumberFormat.INTERNATIONAL,
      NATIONAL: PhoneNumberFormat.NATIONAL,
      RFC3966: PhoneNumberFormat.RFC3966
    };
    return this.transform((value) => {
      if (!value)
        return value;
      try {
        const phone2 = phoneUtil.parseAndKeepRawInput(value, "US");
        const format = phoneUtil.format(phone2, mapping[method]);
        return format;
      } catch (err) {
        return value;
      }
    });
  }
  isUS(message) {
    return this.test("isUS", ({ path }) => message || `${path} must be a number within the United States`, (value) => {
      if (!value)
        return true;
      const phone2 = phoneUtil.parseAndKeepRawInput(value, "US");
      return phoneUtil.isValidNumberForRegion(phone2, "US");
    });
  }
}
const LOAN_METHOD = "loanId";
yup.addMethod(yup.string, LOAN_METHOD, function yupLoan(message) {
  const errMsg = message || "{path} must be a valid Loan ID";
  return this.test(LOAN_METHOD, errMsg, (value) => {
    if (!value)
      return true;
    const loanRegex = /\d{2}-\d{2}-\d{6}$/;
    return loanRegex.test(value);
  });
});
const DECIMAL_METHOD = "percent";
yup.addMethod(yup.number, DECIMAL_METHOD, function yupPercent(message) {
  const errMsg = message || "{path} must be a valid percentage";
  return this.test(DECIMAL_METHOD, errMsg, (value) => {
    if (value === void 0 || value === null)
      return true;
    if (value === 1)
      return true;
    if (value > 1)
      return false;
    if (value === 0)
      return true;
    return value.toString().match(/^\d*\.{1}\d*$/);
  });
});
const ssn = (options2) => new SsnSchemaType(options2 || {
  allowFour: false
});
const money = () => new MoneySchemaType();
const phone = () => new PhoneSchemaType();
const date = (options2) => new DateSchemaType(options2);
export { date, money, phone, ssn };
